// FILE: RequirementsCalculator.js (REVISED FOR ENHANCED ACCURACY v3.0)
// PURPOSE: Calculates detailed costs for independent nonprofits vs. fiscal sponsorship.
// Enforces correct methodology: Admin Overhead % represents core ops costs;
// adds separate discrete costs (now with improved scaling) like Audit, Renewals, Board Mgmt, Compliance/Insurance, and Startup Costs.

import { stateRequirements, multiStateRequirements, federalRequirements } from '../data/stateRequirements';

// --- Constants ---
// Base Service Fees (Review these against market rates)
const COST_STATE_REG_FIRST_SERVICE = 200;
const COST_STATE_REG_ADDITIONAL_SERVICE = 170;
const COST_STATE_RENEWAL_SERVICE_PER_STATE = 125;
const COST_REGISTERED_AGENT_ANNUAL_PER_STATE = 130;
const COST_990EZ_PREP_SERVICE = 2000; // Base for EZ
const COST_990_LONG_PREP_SERVICE_BASE = 3500; // Base for Long Form
const COST_990T_PREP_SERVICE_ADDON = 1000;

// IRS/State Filing Fees (Actuals)
const FEE_IRS_1023_EZ = 275;
const FEE_IRS_1023_FULL = 600;

// Base Costs for Scaling Models (NEW/REVISED)
const BASE_LEGAL_SETUP_LOW_REV = 2000; // For revenue < $100k
const BASE_LEGAL_SETUP_MID_REV = 3500; // For revenue $100k-$1M
const BASE_LEGAL_SETUP_HIGH_REV = 5000; // For revenue > $1M
const BASE_1023_PROF_FEE_LOW_REV = 1500; // Prof fee for Full 1023, revenue < $100k
const BASE_1023_PROF_FEE_MID_REV = 2500; // Prof fee for Full 1023, revenue $100k-$1M
const BASE_AUDIT_LOW_COMPLEXITY = 10000; // Base audit if triggered, lower complexity
const BASE_AUDIT_MID_COMPLEXITY = 15000; // Base audit if triggered, moderate complexity
const BASE_AUDIT_HIGH_COMPLEXITY = 20000; // Base audit if triggered, high complexity (e.g., Single Audit)
const BASE_REVIEW_LOW_COMPLEXITY = 6000; // Base review if triggered
const BASE_REVIEW_MID_COMPLEXITY = 8000; // Base review if triggered
const BASE_INSURANCE_MINIMUM = 1500; // Minimum GL/D&O for very small orgs
const INSURANCE_PER_EMPLOYEE = 85; // Estimate for WC/EPLI add-on per employee
const INSURANCE_PROPERTY_ASSET_FACTOR = 0.001; // Small factor for property insurance based on assets/inventory value (needs asset input refinement)
const BASE_BOARD_MGMT_LOW_REV = 750; // Scaled down for smaller orgs
const BASE_BOARD_MGMT_MID_REV = 1500;

// Complexity Score Weights (Examples - Adjust based on sensitivity analysis)
const COMPLEXITY_WEIGHTS = {
    foreign_accounts: 3,
    investments: 2,
    complex_compensation: 2,
    unrelated_income: 1,
    multi_state: 1,
    many_programs: 1, // e.g., programCount > 5
    high_employee_count: 1, // e.g., employeeCount > 20
    has_inventory: 1,
    complex_funding: 1, // e.g., multiple gov grants
    complex_vendors: 1
};


// --- Helper Function ---
// Simple function to estimate overall complexity score based on factors
const calculateComplexityScore = (factors, states) => {
    let score = 0;
    if (factors.hasForeignAccounts) score += COMPLEXITY_WEIGHTS.foreign_accounts;
    if (factors.hasInvestments) score += COMPLEXITY_WEIGHTS.investments;
    if (factors.hasComplexCompensation) score += COMPLEXITY_WEIGHTS.complex_compensation;
    if (factors.hasUnrelatedIncome) score += COMPLEXITY_WEIGHTS.unrelated_income;
    if (states.length > 1) score += COMPLEXITY_WEIGHTS.multi_state * Math.min(states.length - 1, 3); // Weight multi-state
    if (factors.programCount > 5) score += COMPLEXITY_WEIGHTS.many_programs;
    if (factors.employeeCount > 20) score += COMPLEXITY_WEIGHTS.high_employee_count;
     if (factors.hasInventory) score += COMPLEXITY_WEIGHTS.has_inventory;
     // Funding/Vendor complexity requires more specific input mapping if available
     if (factors.fundingSources?.includes('government') && factors.fundingSources?.length > 2) score += COMPLEXITY_WEIGHTS.complex_funding;
     if (factors.vendorComplexity?.length > 1 && !factors.vendorComplexity?.includes('simple')) score += COMPLEXITY_WEIGHTS.complex_vendors;

     // Cap score for simplicity (e.g., 0-10 range)
     return Math.min(score, 10);
}

// --- Main Calculation Engine ---
export const calculateDetailedRequirements = (
  revenue,
  states = ['CA'], // Default to a state if none provided initially
  grantFunding,
  complexityFactors = {}
) => {

    // --- Input Validation & Defaults ---
    const defaultFactors = {
      hasUnrelatedIncome: false, hasForeignAccounts: false, programCount: 1,
      hasComplexCompensation: false, hasInventory: false, hasInvestments: false,
      employeeCount: 0, volunteerCount: 0, anticipatedGrowth: 'steady',
      subsector: 'other', geographicScope: 'local', staffingModel: 'volunteer',
      inventoryType: ['none'], fundingSources: ['individual'], vendorTransactions: '1-10',
      vendorComplexity: ['simple'], growthStage: 'startup', totalAssets: 0
    };
    const factors = { ...defaultFactors, ...complexityFactors, grantFunding: grantFunding || (complexityFactors.grantFunding || 0) };
    revenue = Number(revenue) || 0;
    grantFunding = Number(factors.grantFunding) || 0;
    factors.programCount = Number(factors.programCount) || 1;
    factors.employeeCount = Number(factors.employeeCount) || 0;
    factors.volunteerCount = Number(factors.volunteerCount) || 0;
    factors.totalAssets = Number(factors.totalAssets) || 0;
    // Ensure states is an array
    if (!Array.isArray(states)) states = [states];
    if (states.length === 0) states = ['CA']; // Ensure at least one state for calculations like stateModifier
    // --- End Input Validation ---

  // === Calculate Overall Complexity Score ===
  const complexityScore = calculateComplexityScore(factors, states); // Score 0-10

  // === Calculate Individual Cost Components (Using Scaled Logic) ===

  // --- STARTUP COSTS ---
  const form1023Costs = calculateForm1023Costs_Scaled(revenue, factors, complexityScore); // Pass complexity
  const legalSetupCosts = calculateLegalSetupCosts_Scaled(revenue, factors, states, complexityScore); // Pass revenue & complexity
  const initialIncorporationFee = calculateInitialIncorporationFee(states); // Unchanged - uses actual state fee
  const stateRegistrationCosts = calculateStateRegistrationCosts(states, revenue); // Unchanged - service fee based on # states

  // --- ANNUAL COSTS ---
  // 1. Core Admin Overhead (Validated Percentage Model) - UNCHANGED
  const adminOverheadData = calculateAdminOverheadPercentage(revenue, states, factors);
  const administrativeOverheadValue = Math.round(revenue * (adminOverheadData.percentage / 100));

  // 2. Discrete Annual Costs (Scaled)
  const auditCosts = calculateAuditCosts_Scaled(revenue, states, grantFunding, factors, complexityScore); // Pass complexity
  const stateRenewalCosts = calculateStateRenewalCosts(states, revenue); // Unchanged - service fee based on # states + RA
  const boardManagementCosts = calculateBoardManagementCosts_Scaled(revenue, factors, states.length); // Pass revenue
  const complianceAndInsuranceCosts = calculateComplianceAndInsuranceCosts_Scaled(revenue, factors, states); // Enhanced scaling

  // 3. Informational Cost (Calculated but NOT added to total) - REVISED scaling
  const form990Costs = calculateForm990Costs_Scaled(revenue, factors, complexityScore); // Pass complexity

  // --- FISCAL SPONSORSHIP COST ---
  const fiscalSponsorshipFee = Math.round(revenue * 0.05);

  // === Calculate Totals ===
  const startupTotal = Math.round(form1023Costs.cost + legalSetupCosts.cost + initialIncorporationFee.cost + stateRegistrationCosts.initialCost);

  // Annual Total = Core Admin Overhead Value + Scaled Discrete Annual Costs
  const annualTotal = Math.round(
        administrativeOverheadValue +
        auditCosts.cost +
        stateRenewalCosts.annualCost +
        boardManagementCosts.cost +
        complianceAndInsuranceCosts.cost
  );

  const totalFirstYear = startupTotal + annualTotal;
  const totalOngoing = annualTotal;

  // === Return Detailed Structure ===
  return {
    startup: {
      form1023: form1023Costs,
      legalSetup: legalSetupCosts,
      initialIncorporationFee: initialIncorporationFee,
      stateRegistrations: stateRegistrationCosts,
      total: startupTotal
    },
    annual: {
      administrativeOverheadPercentage: adminOverheadData, // Breakdown of %
      administrativeOverhead: { cost: administrativeOverheadValue, tooltip: `Estimated core operational costs (staff, accounting, rent, utilities, etc.) based on validated ${adminOverheadData.percentage.toFixed(1)}% overhead rate for similar organizations.` },
      audit: auditCosts,
      stateRenewals: stateRenewalCosts,
      boardManagement: boardManagementCosts,
      complianceAndInsurance: complianceAndInsuranceCosts,
      form990: form990Costs, // Informational
      total: totalOngoing // Total Ongoing = Core Overhead + Discrete Costs
    },
    fiscalSponsorship: {
      totalAnnualFee: fiscalSponsorshipFee,
      administrativeFee: fiscalSponsorshipFee,
      percentage: 5
    },
    stateSpecific: calculateStateSpecificRequirements(states, revenue, factors),
    totalFirstYear: totalFirstYear,
    totalOngoing: totalOngoing,
    // Include complexity score for reference/debugging if needed
    // _debug_complexityScore: complexityScore
  };
};

// --- Helper Calculation Functions (REVISED with _Scaled suffix) ---

// Form 1023 Costs - Scaled professional fees
const calculateForm1023Costs_Scaled = (revenue, factors, complexityScore) => {
    const isEligibleForEZ = revenue < 50000 &&
                            factors.totalAssets < 250000 &&
                            !factors.hasUnrelatedIncome &&
                            !factors.hasForeignAccounts &&
                            factors.programCount <= 3 &&
                            !factors.hasComplexCompensation;

    if (isEligibleForEZ) {
      return { cost: FEE_IRS_1023_EZ, type: '1023-EZ', requirements: ['Gross receipts < $50,000', 'Total assets < $250,000'], tooltip: `IRS Form 1023-EZ application fee ($${FEE_IRS_1023_EZ}). Assumes self-preparation.` };
    }

    // Full Form 1023 - Scaled Professional Fee
    const baseIRSFee = FEE_IRS_1023_FULL;
    let baseProfessionalFee;
    if (revenue < 100000) baseProfessionalFee = BASE_1023_PROF_FEE_LOW_REV; // $1500
    else if (revenue < 1000000) baseProfessionalFee = BASE_1023_PROF_FEE_MID_REV; // $2500
    else baseProfessionalFee = 3500; // Higher base for larger orgs

    // Adjust fee based on complexity score (e.g., +5-15% per point)
    const complexityMultiplier = 1 + (complexityScore * 0.07); // Example: 7% increase per complexity point
    const professionalFees = Math.round(baseProfessionalFee * complexityMultiplier);

    // Cap professional fees (e.g., $1500 - $6000 range)
    const cappedProfessionalFees = Math.max(1500, Math.min(professionalFees, 6000));
    const totalCost = baseIRSFee + cappedProfessionalFees;

    return {
      cost: totalCost,
      type: '1023',
      requirements: ['Detailed narrative', 'Financial projections', 'Bylaws', 'COI policy'],
      tooltip: `Estimated cost ($${totalCost.toLocaleString()}) including IRS fee ($${baseIRSFee}) + scaled professional preparation assistance ($${cappedProfessionalFees.toLocaleString()}) based on revenue and complexity.`
    };
};

// Legal Setup Costs - Scaled based on Revenue and Complexity
const calculateLegalSetupCosts_Scaled = (revenue, factors, states, complexityScore) => {
    let baseCost;
    if (revenue < 100000) baseCost = BASE_LEGAL_SETUP_LOW_REV; // $2000
    else if (revenue < 1000000) baseCost = BASE_LEGAL_SETUP_MID_REV; // $3500
    else baseCost = BASE_LEGAL_SETUP_HIGH_REV; // $5000

    // Adjust base cost by complexity (e.g., 10-20% per point)
    const complexityMultiplier = 1 + (complexityScore * 0.12); // Example: 12% increase per complexity point
    const totalServiceCost = Math.round(baseCost * complexityMultiplier);

    // Cap total cost (e.g., min $2000, max $9000)
    const cappedTotalServiceCost = Math.max(2000, Math.min(totalServiceCost, 9000));

    // Requirements list can be dynamically adjusted based on factors if desired
    let requirements = ['Articles of Incorporation Prep & Filing Support', 'Bylaws Drafting', 'COI Policy Drafting', 'EIN Application Assistance', 'Initial Board Minutes Guidance'];
    if (factors.hasComplexCompensation) requirements.push('Compensation Policy Guidance');
    if (factors.programCount > 5) requirements.push('Program Legal Considerations Review');
    if (factors.inventoryType?.includes('facilities')) requirements.push('Lease Review Counsel');
    if (factors.hasInvestments) requirements.push('Investment Policy Guidance');
    if (factors.hasForeignAccounts) requirements.push('International Operations Counsel');
    if (states.length > 1) requirements.push('Multi-State Compliance Overview');

    return {
      cost: cappedTotalServiceCost,
      requirements: [...new Set(requirements)],
      tooltip: `Estimated average legal service fees ($${cappedTotalServiceCost.toLocaleString()}) for incorporation support, bylaws, initial policies, EIN setup. Scaled by revenue and complexity. Excludes state filing fees.`
    };
};

// Initial State Incorporation Filing Fee(s) - UNCHANGED
const calculateInitialIncorporationFee = (states) => {
    let totalFee = 0;
    const primaryState = states && states.length > 0 ? states[0] : null;
    if (primaryState) {
        totalFee = stateRequirements[primaryState]?.incorporationFee ?? 25; // Use nullish coalescing
    }
    return {
        cost: totalFee,
        tooltip: `State filing fee ($${totalFee.toLocaleString()}) for Articles of Incorporation in ${primaryState || 'primary state'}.`
    };
};


// Form 990 Costs - Service Fees (Scaled - Informational Only)
const calculateForm990Costs_Scaled = (revenue, factors, complexityScore) => {
    let form990Type; let basePrepServiceCost; let finalCost = 0;
    let requirements = []; let tooltip = '';

    if (revenue < 50000) {
      form990Type = '990-N'; basePrepServiceCost = 0; requirements = ['Annual e-Postcard']; tooltip = 'IRS Form 990-N (e-Postcard), $0 service cost assumed.'; finalCost = 0;
    } else if (revenue < 200000 && factors.totalAssets < 500000) {
      form990Type = '990-EZ'; basePrepServiceCost = COST_990EZ_PREP_SERVICE; requirements = ['IRS Form 990-EZ', 'Sch A', 'Sch O']; tooltip = `Est. avg. base service fee ($${basePrepServiceCost.toLocaleString()}) for 990-EZ prep.`; finalCost = basePrepServiceCost;
    } else {
      form990Type = '990'; basePrepServiceCost = COST_990_LONG_PREP_SERVICE_BASE; requirements = ['IRS Form 990', 'Sch A, B, O', 'Detailed financials']; tooltip = `Est. avg. base service fee ($${basePrepServiceCost.toLocaleString()}) for Form 990 prep.`; finalCost = basePrepServiceCost;
    }

    // Add UBIT cost if applicable
    let ubitCostAdded = 0;
    if (form990Type !== '990-N' && factors.hasUnrelatedIncome) {
      ubitCostAdded = COST_990T_PREP_SERVICE_ADDON;
      finalCost += ubitCostAdded;
      requirements.push('Form 990-T (UBIT)');
      tooltip += ` Includes +$${ubitCostAdded.toLocaleString()} service fee for 990-T.`;
    }

    // Apply complexity multiplier (e.g., 5-15% increase per complexity point)
    if (form990Type !== '990-N') {
        const complexityMultiplier = 1 + (complexityScore * 0.08); // Example: 8% per point
        const complexityAdjustment = (basePrepServiceCost * (complexityMultiplier - 1));
        finalCost = basePrepServiceCost + complexityAdjustment + ubitCostAdded;
        if (complexityAdjustment > 0) {
            tooltip += ` Fee adjusted ~$${Math.round(complexityAdjustment).toLocaleString()} based on organizational complexity factors.`;
        }
        // Add specific schedule requirements based on factors for clarity
        if (factors.hasForeignAccounts) requirements.push('Schedule F');
        if (factors.hasInvestments) requirements.push('Schedule D/H complexity');
        if (factors.employeeCount > 10 && factors.hasComplexCompensation) requirements.push('Schedule J complexity');
    }

    // Cap the informational cost (e.g., max $15k for prep)
    finalCost = Math.min(finalCost, 15000);

    return { cost: Math.round(finalCost), type: form990Type, requirements: [...new Set(requirements)], tooltip: tooltip.trim() };
};


// State Registration Costs (Initial Charity Reg Service) - UNCHANGED
const calculateStateRegistrationCosts = (states, revenue) => {
  let initialServiceCost = 0; let requirements = []; const numStates = states ? states.length : 0;
  if (numStates === 0) return { initialCost: 0, requirements: [], tooltip: 'No states selected for charity registration.' };
  initialServiceCost += COST_STATE_REG_FIRST_SERVICE;
  if (numStates > 1) initialServiceCost += (numStates - 1) * COST_STATE_REG_ADDITIONAL_SERVICE;
  requirements.push(`Initial Charitable Solicitation Registration Service (${numStates} state(s))`);
  return {
    initialCost: initialServiceCost, requirements,
    tooltip: `Est. avg. service fee ($${initialServiceCost.toLocaleString()}) for initial charity registration in ${numStates} state(s). Assumes service includes state filing fees.`
  };
};

// State Renewal Costs (Annual Charity Renewal Service + RA) - UNCHANGED
const calculateStateRenewalCosts = (states, revenue) => {
  let totalAnnualCost = 0; let requirements = []; const numStates = states ? states.length : 0;
  let totalRenewalServiceFee = 0; let totalRegAgentFee = 0; let numRenewals = 0; let numRA = 0;
  if (numStates === 0) return { annualCost: 0, requirements: [], tooltip: 'No states selected for renewal/RA.' };
  states.forEach(state => {
    const stateReq = stateRequirements[state];
    // Check for renewal requirement - Use nullish coalescing and check fee > 0
    const needsRenewal = stateReq?.solicitationRequired ?? (stateReq?.renewalFee !== undefined && stateReq.renewalFee !== null); // More robust check
    if (needsRenewal) { totalRenewalServiceFee += COST_STATE_RENEWAL_SERVICE_PER_STATE; numRenewals++; }
    const needsRA = stateReq?.registeredAgentRequired ?? true; // Default true if not specified
    if (needsRA) { totalRegAgentFee += COST_REGISTERED_AGENT_ANNUAL_PER_STATE; numRA++; }
  });
  if (numRenewals > 0) requirements.push(`Annual Charitable Renewal Filing Service (${numRenewals} state(s))`);
  if (numRA > 0) requirements.push(`Registered Agent Service (${numRA} state(s))`);
  totalAnnualCost = totalRenewalServiceFee + totalRegAgentFee;
  return {
    annualCost: totalAnnualCost, requirements: [...new Set(requirements)],
    tooltip: `Est. annual costs ($${totalAnnualCost.toLocaleString()}) including Registered Agent fees ($${totalRegAgentFee.toLocaleString()}) and renewal filing services ($${totalRenewalServiceFee.toLocaleString()}) for required states. State renewal filing fees may be extra.`
  };
};


// Audit Costs - Scaled based on Revenue Tier and Complexity
const calculateAuditCosts_Scaled = (revenue, states, grantFunding, factors, complexityScore) => {
    let cost = 0; let isRequired = false; let auditType = 'None'; let requirements = [];
    let tooltip = 'No audit or independent financial review likely required based on revenue/funding thresholds.';
    const singleAuditThreshold = federalRequirements?.audit?.singleAudit?.threshold || 750000;

    // Determine Base Cost Tier based on Complexity for Audit/Review
    let baseAuditCost;
    let baseReviewCost;
    if (complexityScore <= 3) { // Low complexity
        baseAuditCost = BASE_AUDIT_LOW_COMPLEXITY; // $10k
        baseReviewCost = BASE_REVIEW_LOW_COMPLEXITY; // $6k
    } else if (complexityScore <= 6) { // Mid complexity
        baseAuditCost = BASE_AUDIT_MID_COMPLEXITY; // $15k
        baseReviewCost = BASE_REVIEW_MID_COMPLEXITY; // $8k
    } else { // High complexity
        baseAuditCost = BASE_AUDIT_HIGH_COMPLEXITY; // $20k
        baseReviewCost = 10000; // Higher review cost too
    }

     // Check for Single Audit first (overrides state requirements)
    const requiresSingleAudit = grantFunding >= singleAuditThreshold;
    if (requiresSingleAudit) {
      isRequired = true;
      auditType = 'Single Audit';
      // Single Audit cost is often significantly higher due to compliance testing
      cost = Math.max(baseAuditCost, BASE_AUDIT_HIGH_COMPLEXITY) * 1.2; // Use high complexity base + 20% premium
      requirements.push(`Single Audit (Fed Funds ≥ $${singleAuditThreshold.toLocaleString()})`);
      tooltip = `Single Audit likely required (federal funds ≥ $${singleAuditThreshold.toLocaleString()}). Est. avg. service fee based on complexity: $${Math.round(cost).toLocaleString()}.`;
    } else {
      // Check State Requirements
      const primaryState = states && states.length > 0 ? states[0] : null;
      const stateReq = primaryState ? stateRequirements[primaryState] : null;
      if (stateReq) {
        const stateAuditThreshold = stateReq.auditThreshold;
        const requiresStateAudit = stateAuditThreshold !== null && revenue >= stateAuditThreshold;

        if (requiresStateAudit) {
          isRequired = true;
          auditType = 'Full Audit (State Req)';
          cost = baseAuditCost; // Use complexity-adjusted base
          // Add revenue scaling for very large orgs on top of complexity base
          if (revenue > 10000000) cost *= 1.4; else if (revenue > 5000000) cost *= 1.2;
          requirements.push(`State Audit (${primaryState} Rev ≥ $${stateAuditThreshold.toLocaleString()})`);
          tooltip = `Full financial audit likely required by ${stateName(primaryState)} (rev ≥ $${stateAuditThreshold.toLocaleString()}). Est. avg. service fee based on complexity: $${Math.round(cost).toLocaleString()}.`;
        } else {
          const stateReviewThreshold = stateReq.reviewThreshold;
           const requiresStateReview = stateReviewThreshold !== null && revenue >= stateReviewThreshold;
          if (requiresStateReview) {
            isRequired = true;
            auditType = 'Financial Review (State Req)';
            cost = baseReviewCost; // Use complexity-adjusted base
            requirements.push(`State Review (${primaryState} Rev ≥ $${stateReviewThreshold.toLocaleString()})`);
            tooltip = `Financial review likely required by ${stateName(primaryState)} (rev ≥ $${stateReviewThreshold.toLocaleString()}). Est. avg. service fee based on complexity: $${Math.round(cost).toLocaleString()}.`;
          }
        }
      }
    }
    return { cost: Math.round(cost), isRequired, auditType, requirements, tooltip };
};

// Board Management Costs - Scaled by Revenue Tier
const calculateBoardManagementCosts_Scaled = (revenue, factors, stateCount) => {
    let baseCost;
     if (revenue < 100000) baseCost = BASE_BOARD_MGMT_LOW_REV; // $750
     else if (revenue < 1000000) baseCost = BASE_BOARD_MGMT_MID_REV; // $1500
     else baseCost = 2500; // Higher base for larger orgs

    // Keep simple add-ons for now, could be refined further
    baseCost += (factors.programCount > 5 ? 300 : 0); // Increased threshold
    baseCost += (factors.employeeCount > 25 ? 500 : 0); // Increased threshold
    baseCost += (stateCount > 1 ? 250 * Math.min(stateCount-1, 4) : 0); // Slightly higher multi-state cost
    baseCost += (factors.growthStage === 'scaling' ? 500 : 0); // Higher scaling cost

    const finalCost = Math.round(Math.min(Math.max(750, baseCost), 5500)); // Adjusted cap
    return { cost: finalCost, requirements: ['Board meeting documentation', 'Policy maintenance', 'Compliance tracking'], tooltip: `Est. annual cost ($${finalCost.toLocaleString()}) for board meeting prep, minutes, policy management, governance tools. Scaled by revenue and complexity factors.` };
};

// Compliance & Insurance Costs - More Dynamic Scaling
const calculateComplianceAndInsuranceCosts_Scaled = (revenue, factors, states) => {
    let cost = BASE_INSURANCE_MINIMUM; // Start with $1500 base GL/D&O

    // Add cost based on revenue (e.g., 0.1% - 0.3% of revenue)
    const revenueFactor = Math.max(0.001, Math.min(0.003, revenue / 5000000)); // Example scaling factor
    cost += revenue * revenueFactor;

    let requirements = ['General Liability Insurance', 'Directors & Officers (D&O) Insurance'];
    let tooltip = `Est. annual cost includes baseline GL & D&O ($${BASE_INSURANCE_MINIMUM.toLocaleString()}).`;

    // Add Employee-related insurance costs
    if (factors.employeeCount > 0) {
      const employeeAddOn = (factors.employeeCount * INSURANCE_PER_EMPLOYEE); // $85 per employee
      cost += employeeAddOn;
      requirements.push("Workers' Comp", "EPLI (Recommended)");
      tooltip += ` Includes +~$${Math.round(employeeAddOn).toLocaleString()} for WC/EPLI based on ${factors.employeeCount} employees.`;
    }

    // Add Property/Asset related insurance costs
    // Use totalAssets as proxy, could be refined if specific asset value collected
     if (factors.hasInventory && factors.totalAssets > 10000) { // Only if assets are significant
      const propertyAddOn = 500 + (factors.totalAssets * INSURANCE_PROPERTY_ASSET_FACTOR); // Base + % of assets
      cost += propertyAddOn;
      requirements.push('Property Insurance');
      tooltip += ` Includes +~$${Math.round(propertyAddOn).toLocaleString()} for Property/Assets.`;
    } else if (factors.inventoryType?.includes('facilities')) {
         const facilityAddon = 1000 + (revenue * 0.0005); // Existing logic for facilities if no asset value
         cost += facilityAddon;
         requirements.push('Property Insurance');
         tooltip += ` Includes +~$${Math.round(facilityAddon).toLocaleString()} for Facilities.`;
    }

    // Add other risk factors
    if (factors.volunteerCount > 15) { cost += 500; requirements.push('Volunteer Accident/Liability'); tooltip += ` Includes +~$500 for Volunteer coverage.`; }
    if (factors.hasForeignAccounts) { cost += 1200; requirements.push("Intl Compliance/Insurance"); tooltip += ` Includes +~$1.2k for Intl Ops.`; }
    if (states.length > 1) { cost += 400 * Math.min(states.length -1, 3) ; requirements.push("Multi-State Monitoring"); tooltip += ` Includes +~$${400 * Math.min(states.length -1, 3)} for Multi-State Monitoring.`; }
    // Consider adding risk based on subsector if data available (e.g., health, human services might have higher liability)

    const finalCost = Math.round(Math.max(1500, cost)); // Ensure minimum cost
    tooltip += ` Total Estimated: $${finalCost.toLocaleString()}. Varies widely based on specific risks, activities, and carrier underwriting.`
    return { cost: finalCost, requirements: [...new Set(requirements)], tooltip: tooltip };
};

// State Specific Requirements Flags - UNCHANGED (Logic depends on state data)
const calculateStateSpecificRequirements = (states, revenue, factors) => {
    const primaryState = states && states.length > 0 ? states[0] : null;
    const stateReqData = primaryState ? stateRequirements[primaryState] : {};
    const grantFunding = factors.grantFunding || 0;
    const stateAuditThreshold = stateReqData?.auditThreshold;
    const stateReviewThreshold = stateReqData?.reviewThreshold;

    // Ensure thresholds are numbers or null
     const auditThresh = (typeof stateAuditThreshold === 'number') ? stateAuditThreshold : null;
     const reviewThresh = (typeof stateReviewThreshold === 'number') ? stateReviewThreshold : null;

    const requiresStateAudit = auditThresh !== null && revenue >= auditThresh;
    const requiresStateReview = !requiresStateAudit && reviewThresh !== null && revenue >= reviewThresh;

    const singleAuditThreshold = federalRequirements?.audit?.singleAudit?.threshold || 750000;
    const requiresSingleAudit = grantFunding >= singleAuditThreshold;

    const reqs = {
        initialRegistration: stateReqData?.solicitationRequired ?? (stateReqData?.initialFee > 0),
        annualRenewal: stateReqData?.renewalFrequency === 'Annual' || (stateReqData?.renewalFee > 0) || stateReqData?.solicitationRequired,
        auditRequired: requiresStateAudit || requiresSingleAudit,
        financialReviewRequired: requiresStateReview,
        stateRegistrationRequired: stateReqData?.solicitationRequired ?? (stateReqData?.initialFee > 0), // Redundant? Same as initialRegistration
        secretaryOfStateRequired: true, // Generally true for incorporated entities
        registeredAgentRequired: stateReqData?.registeredAgentRequired ?? true
    };
    return { requirements: reqs };
};

// Helper to get state name for tooltips
const stateName = (code) => stateRequirements[code]?.name || code || 'N/A';


// --- CORE ADMIN OVERHEAD % CALCULATION --- (UNCHANGED - Based on Validation Script)
export const calculateAdminOverheadPercentage = (revenue, states, factors = {}) => {
    const calcFactors = {
      hasUnrelatedIncome: factors.hasUnrelatedIncome ?? false, hasForeignAccounts: factors.hasForeignAccounts ?? false,
      programCount: Number(factors.programCount) || 1, hasComplexCompensation: factors.hasComplexCompensation ?? false,
      hasInventory: factors.hasInventory ?? false, hasInvestments: factors.hasInvestments ?? false,
      employeeCount: Number(factors.employeeCount) || 0, subsector: factors.subsector || 'other',
    };
  let basePercentage;
  if (revenue < 100000) basePercentage = 22.7; else if (revenue < 500000) basePercentage = 17.6;
  else if (revenue < 1000000) basePercentage = 16.5; else basePercentage = 15.0;
  const randomVariance = (Math.random() * 0.6) - 0.3; let calculatedBase = basePercentage + randomVariance;
  let complexityModifier = 0;
  if (calcFactors.hasUnrelatedIncome) complexityModifier += 1.5; if (calcFactors.hasForeignAccounts) complexityModifier += 2.0;
  if (calcFactors.programCount > 3) complexityModifier += 1.0; if (calcFactors.hasComplexCompensation) complexityModifier += 1.5;
  if (calcFactors.hasInventory) complexityModifier += 0.7; if (calcFactors.hasInvestments) complexityModifier += 1.0;
  if (calcFactors.employeeCount > 10) complexityModifier += 1.5;
  const highRegulationStates = ['CA', 'NY', 'MA']; const mediumRegulationStates = ['FL', 'PA', 'OH', 'MI', 'NJ', 'IL', 'MD', 'VA', 'WA'];
  const lowRegulationStates = ['TX', 'GA', 'AZ', 'CO', 'TN', 'NV', 'NC', 'UT', 'SC']; let stateModifier = 0;
  const primaryState = states && states.length > 0 ? states[0] : null;
  if (primaryState) {
      if (highRegulationStates.includes(primaryState)) stateModifier = 1.4; else if (mediumRegulationStates.includes(primaryState)) stateModifier = 0.6;
      else if (lowRegulationStates.includes(primaryState)) stateModifier = -0.3; else stateModifier = 0.2;
  } else { stateModifier = 0.2; }
  let subsectorModifier = 0; const subsector = calcFactors.subsector?.toLowerCase();
  if (subsector === 'arts') subsectorModifier = -1.7; else if (subsector === 'education') subsectorModifier = -0.4; else if (subsector === 'health') subsectorModifier = 1.7;
  else if (subsector === 'international') subsectorModifier = 2.2; else if (subsector === 'environment') subsectorModifier = -0.8; else if (subsector === 'human_services') subsectorModifier = 0.4;
  else if (subsector === 'public_societal_benefit') subsectorModifier = 0.5; else if (subsector === 'religion') subsectorModifier = -0.5; else subsectorModifier = 0;
  const finalPercentage = calculatedBase + complexityModifier + stateModifier + subsectorModifier; const cappedPercentage = Math.max(15, Math.min(25, finalPercentage));
  return { percentage: cappedPercentage, basePercentage: calculatedBase, complexityModifier: complexityModifier, stateModifier: stateModifier, subsectorModifier: subsectorModifier };
};

// --- Exports ---
// Export main function and potentially helpers if used elsewhere (though likely not needed)
// export { calculateDetailedRequirements };
// export { calculateForm1023Costs_Scaled, calculateLegalSetupCosts_Scaled, ... }; // etc.